import type { LocalizedCopy } from "../productCopyTypes";

function paragraphs(text: string): string {
  return text
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>\n${p}\n</p>`)
    .join("\n\n");
}

function featureList(items: string[]): string {
  return `<ul>\n${items.map((item) => `  <li>${item}</li>`).join("\n")}\n</ul>`;
}

const COPY = {
  en: {
    title: "Lynx Tactical ATV",
    description:
      "Lynx Tactical ATV is a detailed military-themed brick set designed for builders and collectors who appreciate modern tactical vehicles and functional construction. With 737+ pieces and a recommended age of 14+, the set combines a rugged 6x6-style vehicle design with a detachable cargo trailer and a range of interchangeable equipment.\n\nThe model features a distinctive armored-style front section, reinforced side panels, large off-road wheels and a compact tactical layout. The open rear area is connected to a dedicated trailer carrying additional equipment and storage containers, giving the finished set a convincing field-support appearance.\n\nOne of the most interesting elements is the interchangeable equipment module positioned behind the cab. The supplied configuration can be transformed into a different equipment arrangement, allowing the vehicle to take on different operational roles. The model also features a roof-mounted machine gun, communication antennas and additional tactical equipment placed around the vehicle.\n\nThe trailer adds another layer of functionality to the build. It provides additional cargo space and can be separated from the main vehicle, creating two independent display elements. The construction uses a combination of sand, green and dark elements to create a military-inspired camouflage scheme.\n\nThe Lynx Tactical ATV is a compact yet highly detailed set that works equally well as a display model and as an enjoyable building project. Its modular equipment, detachable trailer and military styling make it an interesting addition to modern military and tactical vehicle collections.",
    historicalTitle: "Technical Background",
    historical:
      "The Lynx Tactical ATV is presented as a modern tactical support vehicle rather than a historical military reproduction. Its design combines characteristics commonly associated with modern field vehicles, including high ground clearance, large off-road wheels, compact protected crew areas, communications equipment and modular cargo capability.\n\nThe modular equipment concept reflects a broader trend in modern military vehicle design, where a common platform can be adapted to different tasks by changing mission equipment. Cargo transport, communications, reconnaissance or field-support roles can all benefit from this approach.\n\nThe set translates these concepts into a brick-built format. The detachable trailer expands the vehicle's carrying capability, while the interchangeable equipment section provides an additional layer of customization. Together with the roof-mounted weapon station, antennas and storage elements, these details create a vehicle that looks suited to a wide range of fictional or modern military scenarios.",
    highlightsTitle: "Product Features",
    features: [
      "Brand: 12GO",
      "Set Number: 91013",
      "Name: Lynx Tactical ATV",
      "Pieces: 737+",
      "Recommended Age: 14+",
      "Tactical military vehicle design",
      "Six-wheel vehicle configuration",
      "Detachable cargo trailer",
      "Interchangeable equipment module",
      "Roof-mounted machine gun",
      "Communication antennas",
      "Off-road wheels",
      "Detailed cab construction",
      "Cargo and storage equipment",
      "Military-inspired camouflage",
      "Suitable for display and collection",
    ],
    specsTitle: "Specifications",
    specs: [
      "<li><strong>Brand:</strong> 12GO</li>",
      "<li><strong>Set Number:</strong> 91013</li>",
      "<li><strong>Name:</strong> Lynx Tactical ATV</li>",
      "<li><strong>Pieces:</strong> 737+</li>",
      "<li><strong>Recommended Age:</strong> 14+</li>",
    ],
  },
  pl: {
    title: "Lynx Tactical ATV",
    description:
      "Lynx Tactical ATV to szczegółowy militarny set z klocków przeznaczony dla osób zainteresowanych współczesnymi pojazdami taktycznymi. Zestaw składa się z ponad 737 elementów i jest przeznaczony dla budujących w wieku 14+. Łączy charakterystyczną sylwetkę terenowego pojazdu wojskowego z odpinaną przyczepą oraz modułowym wyposażeniem.\n\nModel otrzymał rozbudowaną przednią część inspirowaną pojazdami opancerzonymi, wzmocnione osłony boczne, duże terenowe koła oraz kompaktową konstrukcję taktyczną. Tylna część pojazdu współpracuje z osobną przyczepą transportową wyposażoną w dodatkowe wyposażenie i pojemniki.\n\nJednym z najciekawszych elementów jest wymienny moduł wyposażenia umieszczony za kabiną. Pozwala on zmieniać konfigurację pojazdu i dostosować go do różnych zastosowań. Na dachu znajduje się również stanowisko z karabinem maszynowym, a całość uzupełniają anteny komunikacyjne oraz dodatkowe wyposażenie taktyczne.\n\nPrzyczepa zapewnia dodatkową przestrzeń transportową i może zostać odłączona od głównego pojazdu, dzięki czemu zestaw oferuje dwie niezależne konstrukcje do ekspozycji. Kolorystyka oparta na odcieniach piaskowym, zielonym i ciemnym nadaje całości charakterystyczny militarny wygląd.\n\nLynx Tactical ATV to kompaktowy, ale bardzo szczegółowy set, który dobrze sprawdzi się zarówno jako element kolekcji, jak i jako ciekawy projekt konstrukcyjny. Modułowe wyposażenie, odpinana przyczepa i nowoczesna stylistyka czynią go interesującą propozycją dla kolekcjonerów współczesnych pojazdów militarnych.",
    historicalTitle: "Tło techniczne",
    historical:
      "Lynx Tactical ATV jest przedstawiony jako współczesny pojazd taktycznego wsparcia, a nie historyczna replika konkretnego pojazdu. Jego konstrukcja łączy rozwiązania charakterystyczne dla nowoczesnych pojazdów terenowych, takie jak duży prześwit, terenowe koła, kompaktowa kabina, wyposażenie komunikacyjne oraz możliwość transportu dodatkowego ładunku.\n\nModułowa konstrukcja wyposażenia nawiązuje do współczesnego podejścia do projektowania pojazdów wojskowych. Wspólna platforma może zostać dostosowana do różnych zadań poprzez zmianę wyposażenia misyjnego.\n\nW przypadku tego setu rozwiązanie to zostało odwzorowane w formie klockowej. Odpinana przyczepa zwiększa możliwości transportowe, natomiast wymienny moduł pozwala zmieniać konfigurację pojazdu. W połączeniu z karabinem maszynowym, antenami i wyposażeniem dodatkowym tworzy to charakterystyczny pojazd przeznaczony do scenariuszy taktycznych i ekspozycji.",
    highlightsTitle: "Cechy produktu",
    features: [
      "Marka: 12GO",
      "Numer zestawu: 91013",
      "Nazwa: Lynx Tactical ATV",
      "Liczba elementów: 737+",
      "Wiek: 14+",
      "Konstrukcja inspirowana pojazdem taktycznym",
      "Układ z sześcioma kołami",
      "Odpinana przyczepa transportowa",
      "Wymienny moduł wyposażenia",
      "Karabin maszynowy na dachu",
      "Anteny komunikacyjne",
      "Duże terenowe koła",
      "Szczegółowa kabina",
      "Wyposażenie transportowe i magazynowe",
      "Militarny schemat kolorystyczny",
      "Przeznaczony do ekspozycji i kolekcjonowania",
    ],
    specsTitle: "Specyfikacja",
    specs: [
      "<li><strong>Marka:</strong> 12GO</li>",
      "<li><strong>Numer zestawu:</strong> 91013</li>",
      "<li><strong>Nazwa:</strong> Lynx Tactical ATV</li>",
      "<li><strong>Liczba elementów:</strong> 737+</li>",
      "<li><strong>Wiek:</strong> 14+</li>",
    ],
  },
  de: {
    title: "Lynx Tactical ATV",
    description:
      "Lynx Tactical ATV ist ein detailliertes militärisch inspiriertes Klemmbaustein-Set für Fans moderner taktischer Fahrzeuge. Mit 737+ Teilen und einer Altersempfehlung von 14+ Jahren kombiniert das Set ein robustes Geländefahrzeug mit einem abnehmbaren Transportanhänger und modularer Ausrüstung.\n\nDas Modell besitzt eine markante Frontpartie im Stil moderner geschützter Fahrzeuge, verstärkte Seitenbereiche, große Geländereifen und eine kompakte taktische Konstruktion. Der hintere Bereich ist mit einem separaten Anhänger verbunden, der zusätzlichen Stauraum und Ausrüstung bietet.\n\nEin besonderes Detail ist das wechselbare Ausrüstungsmodul hinter der Kabine. Dadurch kann die Konfiguration des Fahrzeugs verändert werden. Ergänzt wird das Modell durch ein Maschinengewehr auf dem Dach, Kommunikationsantennen und weitere taktische Ausrüstung.\n\nDer Anhänger kann vom Hauptfahrzeug getrennt werden und bildet damit ein zusätzliches eigenständiges Display-Element. Die Kombination aus Sand-, Grün- und dunklen Farbtönen verleiht dem Set eine charakteristische militärische Optik.",
    historicalTitle: "Technischer Hintergrund",
    historical:
      "Das Lynx Tactical ATV ist als modernes taktisches Unterstützungsfahrzeug konzipiert und keine historische Reproduktion eines bestimmten Militärfahrzeugs. Das Design verbindet typische Merkmale moderner Geländefahrzeuge mit geschützter Besatzungsunterbringung, Kommunikationstechnik und modularer Transportkapazität.\n\nDie modulare Ausrüstung spiegelt ein Konzept wider, das bei modernen Militärfahrzeugen zunehmend an Bedeutung gewinnt: Eine gemeinsame Plattform kann durch unterschiedliche Missionsausrüstung für verschiedene Aufgaben angepasst werden.\n\nDas Set überträgt dieses Konzept auf eine Klemmbaustein-Konstruktion. Der abnehmbare Anhänger erweitert die Transportmöglichkeiten, während das Wechselmodul zusätzliche Konfigurationsmöglichkeiten bietet. Zusammen mit Maschinengewehr, Antennen und Zusatzausrüstung entsteht ein vielseitiges taktisches Fahrzeug für die Sammlung und Ausstellung.",
    highlightsTitle: "Produkteigenschaften",
    features: [
      "Marke: 12GO",
      "Setnummer: 91013",
      "Name: Lynx Tactical ATV",
      "Teile: 737+",
      "Altersempfehlung: 14+",
      "Modernes taktisches Fahrzeugdesign",
      "Sechsrad-Konfiguration",
      "Abnehmbarer Transportanhänger",
      "Wechselbares Ausrüstungsmodul",
      "Maschinengewehr auf dem Dach",
      "Kommunikationsantennen",
      "Große Geländereifen",
      "Detaillierte Kabine",
      "Transport- und Stauraumausrüstung",
      "Militärische Farbgebung",
      "Für Ausstellung und Sammlung geeignet",
    ],
    specsTitle: "Spezifikationen",
    specs: [
      "<li><strong>Marke:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 91013</li>",
      "<li><strong>Name:</strong> Lynx Tactical ATV</li>",
      "<li><strong>Teile:</strong> 737+</li>",
      "<li><strong>Altersempfehlung:</strong> 14+</li>",
    ],
  },
  fr: {
    title: "Lynx Tactical ATV",
    description:
      "Lynx Tactical ATV est un set en briques détaillé inspiré des véhicules tactiques modernes. Avec plus de 737 pièces et une recommandation d'âge de 14 ans et plus, il combine un véhicule tout-terrain militaire, une remorque amovible et un équipement modulaire.\n\nLe modèle présente une partie avant robuste inspirée des véhicules protégés modernes, de grands pneus tout-terrain et une construction compacte. Une remorque de transport complète le véhicule et permet d'ajouter du matériel et des éléments de stockage.\n\nL'un des principaux éléments du set est le module d'équipement interchangeable situé derrière la cabine. Sa configuration peut être modifiée afin de donner au véhicule une apparence différente. Le modèle comprend également une mitrailleuse montée sur le toit, des antennes de communication et divers équipements tactiques.\n\nLa remorque peut être séparée du véhicule principal, offrant ainsi une construction supplémentaire pour l'exposition. Les couleurs sable, vertes et foncées renforcent son style militaire.",
    historicalTitle: "Contexte technique",
    historical:
      "Le Lynx Tactical ATV est présenté comme un véhicule moderne de soutien tactique et non comme la reproduction historique d'un véhicule précis. Son design reprend plusieurs caractéristiques communes aux véhicules militaires modernes : grande garde au sol, roues tout-terrain, cabine compacte, moyens de communication et capacité de transport modulaire.\n\nLe concept d'équipement interchangeable correspond à une approche moderne permettant d'adapter une même plateforme à différentes missions en modifiant son équipement.\n\nDans ce set, cette idée est reproduite sous la forme d'une construction en briques. La remorque amovible augmente la capacité de transport, tandis que le module interchangeable permet de modifier la configuration du véhicule.",
    highlightsTitle: "Caractéristiques",
    features: [
      "Marque : 12GO",
      "Référence : 91013",
      "Nom : Lynx Tactical ATV",
      "Nombre de pièces : 737+",
      "Âge recommandé : 14+",
      "Design de véhicule tactique moderne",
      "Configuration à six roues",
      "Remorque amovible",
      "Module d'équipement interchangeable",
      "Mitrailleuse montée sur le toit",
      "Antennes de communication",
      "Grandes roues tout-terrain",
      "Cabine détaillée",
      "Équipement de transport et de stockage",
      "Coloris d'inspiration militaire",
      "Idéal pour exposition et collection",
    ],
    specsTitle: "Spécifications",
    specs: [
      "<li><strong>Marque :</strong> 12GO</li>",
      "<li><strong>Référence :</strong> 91013</li>",
      "<li><strong>Nom :</strong> Lynx Tactical ATV</li>",
      "<li><strong>Nombre de pièces :</strong> 737+</li>",
      "<li><strong>Âge recommandé :</strong> 14+</li>",
    ],
  },
  es: {
    title: "Lynx Tactical ATV",
    description:
      "Lynx Tactical ATV es un set de bloques detallado inspirado en los modernos vehículos tácticos. Con más de 737 piezas y una edad recomendada de 14+, combina un vehículo todoterreno militar con un remolque desmontable y un sistema de equipamiento modular.\n\nEl modelo presenta una robusta sección delantera inspirada en vehículos protegidos, grandes ruedas todoterreno y una estructura compacta. El remolque añade capacidad de transporte y espacio para equipamiento y contenedores.\n\nUno de los elementos más interesantes es el módulo de equipamiento intercambiable situado detrás de la cabina. Su configuración puede modificarse para adaptar el aspecto del vehículo. También incluye una ametralladora montada en el techo, antenas de comunicación y otros elementos tácticos.\n\nEl remolque puede separarse del vehículo principal, creando una segunda pieza para exposición. La combinación de tonos arena, verde y oscuros proporciona al modelo una estética militar característica.",
    historicalTitle: "Contexto técnico",
    historical:
      "El Lynx Tactical ATV se presenta como un vehículo moderno de apoyo táctico y no como una reproducción histórica de un vehículo concreto. Su diseño incorpora elementos habituales de los vehículos militares actuales, como gran altura libre al suelo, ruedas todoterreno, una cabina compacta y capacidad de transporte modular.\n\nEl concepto de equipamiento intercambiable refleja una tendencia en el diseño de vehículos modernos, donde una misma plataforma puede adaptarse a diferentes tareas mediante cambios en el equipamiento de misión.\n\nEl set traslada esta idea al mundo de los bloques. El remolque desmontable amplía la capacidad de transporte, mientras que el módulo intercambiable permite modificar la configuración del vehículo.",
    highlightsTitle: "Características",
    features: [
      "Marca: 12GO",
      "Número de set: 91013",
      "Nombre: Lynx Tactical ATV",
      "Piezas: 737+",
      "Edad recomendada: 14+",
      "Diseño de vehículo táctico moderno",
      "Configuración de seis ruedas",
      "Remolque desmontable",
      "Módulo de equipamiento intercambiable",
      "Ametralladora montada en el techo",
      "Antenas de comunicación",
      "Grandes ruedas todoterreno",
      "Cabina detallada",
      "Equipamiento de transporte y almacenamiento",
      "Esquema de colores militar",
      "Ideal para exposición y colección",
    ],
    specsTitle: "Especificaciones",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Número de set:</strong> 91013</li>",
      "<li><strong>Nombre:</strong> Lynx Tactical ATV</li>",
      "<li><strong>Piezas:</strong> 737+</li>",
      "<li><strong>Edad recomendada:</strong> 14+</li>",
    ],
  },
  nl: {
    title: "Lynx Tactical ATV",
    description:
      "Lynx Tactical ATV is een gedetailleerd bouwstenenmodel geïnspireerd op moderne tactische voertuigen. Met 737+ onderdelen en een aanbevolen leeftijd van 14+ combineert het model een robuust militair terreinvoertuig met een afneembare aanhanger en modulaire uitrusting.\n\nHet voertuig beschikt over een stevige voorzijde, grote terreinbanden, versterkte zijpanelen en een compacte tactische opbouw. De aanhanger biedt extra ruimte voor transport en opslag.\n\nEen opvallend element is de verwisselbare uitrustingsmodule achter de cabine. Hiermee kan de configuratie van het voertuig worden aangepast. Een machinegeweer op het dak, communicatieantennes en aanvullende tactische uitrusting maken het ontwerp compleet.\n\nDe aanhanger kan van het voertuig worden losgekoppeld en vormt zo een afzonderlijk displayonderdeel. De combinatie van zandkleurige, groene en donkere elementen zorgt voor een herkenbare militaire uitstraling.",
    historicalTitle: "Technische achtergrond",
    historical:
      "De Lynx Tactical ATV is ontworpen als een modern tactisch ondersteuningsvoertuig en niet als historische replica van een specifiek voertuig. Het ontwerp combineert kenmerken van moderne militaire terreinvoertuigen, waaronder grote bodemvrijheid, terreinwielen, compacte bemanningsruimte en modulaire transportmogelijkheden.\n\nDe verwisselbare uitrustingsmodule sluit aan bij een modern ontwerpprincipe waarbij een gemeenschappelijk voertuigplatform voor verschillende taken kan worden aangepast.\n\nIn dit set wordt dat concept vertaald naar een modulaire bouwconstructie. De afneembare aanhanger vergroot de transportcapaciteit, terwijl de verwisselbare module extra mogelijkheden voor configuratie en display biedt.",
    highlightsTitle: "Productkenmerken",
    features: [
      "Merk: 12GO",
      "Setnummer: 91013",
      "Naam: Lynx Tactical ATV",
      "Onderdelen: 737+",
      "Aanbevolen leeftijd: 14+",
      "Modern tactisch voertuigontwerp",
      "Configuratie met zes wielen",
      "Afneembare transportaanhanger",
      "Verwisselbare uitrustingsmodule",
      "Machinegeweer op het dak",
      "Communicatieantennes",
      "Grote terreinbanden",
      "Gedetailleerde cabine",
      "Transport- en opslaguitrusting",
      "Militaire kleurstelling",
      "Geschikt voor display en collectie",
    ],
    specsTitle: "Specificaties",
    specs: [
      "<li><strong>Merk:</strong> 12GO</li>",
      "<li><strong>Setnummer:</strong> 91013</li>",
      "<li><strong>Naam:</strong> Lynx Tactical ATV</li>",
      "<li><strong>Onderdelen:</strong> 737+</li>",
      "<li><strong>Aanbevolen leeftijd:</strong> 14+</li>",
    ],
  },
  it: {
    title: "Lynx Tactical ATV",
    description:
      "Lynx Tactical ATV è un set premium in mattoncini ispirato ai moderni veicoli tattici militari. Con 737+ pezzi e un'età consigliata di 14+, combina un veicolo fuoristrada militare con un rimorchio removibile e un sistema di equipaggiamento modulare.\n\nIl modello presenta una robusta sezione anteriore, grandi ruote fuoristrada, pannelli laterali rinforzati e una struttura compatta. Il rimorchio aggiunge capacità di trasporto e spazio per equipaggiamento e contenitori.\n\nUno degli elementi principali è il modulo di equipaggiamento intercambiabile posizionato dietro la cabina. La configurazione può essere modificata per adattare l'aspetto del veicolo. Sono inoltre presenti una mitragliatrice montata sul tetto, antenne di comunicazione e altri dettagli tattici.\n\nIl rimorchio può essere separato dal veicolo principale e utilizzato come elemento indipendente da esposizione. La combinazione di colori sabbia, verde e scuri conferisce al modello un caratteristico stile militare.",
    historicalTitle: "Contesto tecnico",
    historical:
      "Il Lynx Tactical ATV è presentato come un moderno veicolo di supporto tattico e non come la riproduzione storica di un mezzo specifico. Il design combina elementi tipici dei veicoli militari moderni, come grande altezza da terra, ruote fuoristrada, cabina compatta e capacità di trasporto modulare.\n\nIl concetto di equipaggiamento intercambiabile riflette l'approccio moderno alla progettazione dei veicoli militari, nel quale una piattaforma comune può essere adattata a compiti differenti.\n\nIl set riproduce questa idea attraverso una struttura modulare in mattoncini. Il rimorchio removibile aumenta la capacità di trasporto, mentre il modulo intercambiabile permette di modificare la configurazione del veicolo.",
    highlightsTitle: "Caratteristiche",
    features: [
      "Marca: 12GO",
      "Numero set: 91013",
      "Nome: Lynx Tactical ATV",
      "Pezzi: 737+",
      "Età consigliata: 14+",
      "Design da veicolo tattico moderno",
      "Configurazione a sei ruote",
      "Rimorchio removibile",
      "Modulo di equipaggiamento intercambiabile",
      "Mitragliatrice montata sul tetto",
      "Antenne di comunicazione",
      "Grandi ruote fuoristrada",
      "Cabina dettagliata",
      "Equipaggiamento per trasporto e stoccaggio",
      "Colorazione di ispirazione militare",
      "Ideale per esposizione e collezione",
    ],
    specsTitle: "Specifiche",
    specs: [
      "<li><strong>Marca:</strong> 12GO</li>",
      "<li><strong>Numero set:</strong> 91013</li>",
      "<li><strong>Nome:</strong> Lynx Tactical ATV</li>",
      "<li><strong>Pezzi:</strong> 737+</li>",
      "<li><strong>Età consigliata:</strong> 14+</li>",
    ],
  },
  ru: {
    title: "Lynx Tactical ATV",
    description:
      "Lynx Tactical ATV — детализированный набор из конструктора, созданный в стиле современных тактических военных машин. Набор содержит 737+ деталей и предназначен для пользователей от 14 лет.\n\nМодель сочетает массивную переднюю часть, большие внедорожные колёса, усиленные боковые панели и отдельный грузовой прицеп. Прицеп обеспечивает дополнительное пространство для перевозки оборудования и контейнеров.\n\nОсобое внимание уделено сменному модулю оборудования, расположенному за кабиной. Конфигурацию можно изменять, что позволяет создавать разные варианты внешнего вида машины. Дополняют модель пулемёт на крыше, антенны связи и дополнительное тактическое оборудование.\n\nПрицеп можно отсоединить от основного автомобиля, благодаря чему он также может использоваться как отдельный элемент экспозиции.",
    historicalTitle: "Техническая концепция",
    historical:
      "Lynx Tactical ATV представлен как современная тактическая машина поддержки, а не как историческая копия конкретного военного автомобиля. Конструкция объединяет характерные особенности современных военных транспортных средств: высокий дорожный просвет, внедорожные колёса, компактную кабину, оборудование связи и модульные транспортные возможности.\n\nКонцепция сменного оборудования отражает современный подход к проектированию военных машин, при котором одна базовая платформа может адаптироваться под различные задачи.\n\nВ этом наборе данный принцип реализован через модульную конструкцию. Съёмный прицеп увеличивает транспортные возможности, а сменный модуль позволяет изменять конфигурацию машины.",
    highlightsTitle: "Характеристики",
    features: [
      "Бренд: 12GO",
      "Номер набора: 91013",
      "Название: Lynx Tactical ATV",
      "Количество деталей: 737+",
      "Рекомендуемый возраст: 14+",
      "Современный тактический дизайн",
      "Шестиколёсная конфигурация",
      "Съёмный грузовой прицеп",
      "Сменный модуль оборудования",
      "Пулемёт на крыше",
      "Антенны связи",
      "Большие внедорожные колёса",
      "Детализированная кабина",
      "Дополнительное транспортное оборудование",
      "Военная цветовая схема",
      "Подходит для экспонирования и коллекционирования",
    ],
    specsTitle: "Характеристики набора",
    specs: [
      "<li><strong>Бренд:</strong> 12GO</li>",
      "<li><strong>Номер набора:</strong> 91013</li>",
      "<li><strong>Название:</strong> Lynx Tactical ATV</li>",
      "<li><strong>Количество деталей:</strong> 737+</li>",
      "<li><strong>Рекомендуемый возраст:</strong> 14+</li>",
    ],
  },
  zh: {
    title: "Lynx Tactical ATV",
    description:
      "Lynx Tactical ATV 是一款以现代战术军用车辆为灵感设计的高细节积木套装。整套包含 737+ 个积木零件，建议 14 岁以上使用，结合了越野战术车辆、可拆卸货运拖车以及模块化装备设计。\n\n模型采用坚固的前部结构、大尺寸越野轮胎、强化侧面装甲和紧凑的战术车辆布局。后部连接独立的货运拖车，可用于搭载额外设备和储物箱。\n\n模型的一大特点是驾驶舱后方的可替换装备模块。通过更换模块，可以改变车辆的配置和外观。车顶配备机枪，车体上还加入了通信天线以及其他战术装备细节。\n\n货运拖车可以从主车上拆下，并作为独立展示部件使用。沙色、绿色和深色元素组合形成具有军事风格的整体配色。",
    historicalTitle: "技术背景",
    historical:
      "Lynx Tactical ATV 定位为现代战术支援车辆，并非某一具体历史车辆的复制品。其设计融合了现代军用越野车辆常见的多种特点，例如较高的离地间隙、越野轮胎、紧凑驾驶舱、通信设备以及模块化运输能力。\n\n可替换装备模块体现了现代军用车辆设计中的模块化理念，即使用同一基础平台，通过更换任务装备来适应不同的用途。\n\n该套装通过积木结构将这一理念进行了还原。可拆卸拖车增强了运输能力，而可替换装备模块则进一步增加了车辆的可变性和收藏展示价值。",
    highlightsTitle: "产品特点",
    features: [
      "品牌： 12GO",
      "产品编号： 91013",
      "名称： Lynx Tactical ATV",
      "积木数量： 737+ 块",
      "建议年龄： 14 岁以上",
      "现代战术车辆设计",
      "六轮车辆结构",
      "可拆卸货运拖车",
      "可替换装备模块",
      "车顶机枪",
      "通信天线",
      "大型越野轮胎",
      "精细驾驶舱",
      "运输及储物设备",
      "军事风格配色",
      "适合展示与收藏",
    ],
    specsTitle: "产品规格",
    specs: [
      "<li><strong>品牌：</strong> 12GO</li>",
      "<li><strong>产品编号：</strong> 91013</li>",
      "<li><strong>名称：</strong> Lynx Tactical ATV</li>",
      "<li><strong>积木数量：</strong> 737+ 块</li>",
      "<li><strong>建议年龄：</strong> 14 岁以上</li>",
    ],
  },
} as const;

type LocaleKey = keyof typeof COPY;

function buildLocaleHtml(locale: LocaleKey): string {
  const c = COPY[locale] ?? COPY.en;
  return `<h1>${c.title}</h1>

${paragraphs(c.description)}

<h2>${c.specsTitle}</h2>

<ul>
${c.specs.join("\n")}
</ul>

<h2>${c.highlightsTitle}</h2>

${featureList([...c.features])}

<h2>${c.historicalTitle}</h2>

${paragraphs(c.historical)}`;
}

export const RICH_PRODUCT_COPY_91013: LocalizedCopy = {
  en: buildLocaleHtml("en"),
  pl: buildLocaleHtml("pl"),
  de: buildLocaleHtml("de"),
  es: buildLocaleHtml("es"),
  fr: buildLocaleHtml("fr"),
  nl: buildLocaleHtml("nl"),
  zh: buildLocaleHtml("zh"),
  it: buildLocaleHtml("it"),
  ru: buildLocaleHtml("ru"),
};
